import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  Chip,
  FormControl,
  OutlinedInput,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { serverAddr, uploadServer } from "../../networkConfig";
import "./LogicModal.scss";

const TAGS = {
  Intraday: {
    long: [
      "Breakout", "Support Bounce", "Momentum Push", "High Volume", "RSI Reversal",
      "EMA Flip", "VWAP Reclaim", "Range Expansion", "Trend Continuation", "BTC Strength",
      "Alt Rotation", "Short Covering", "Volume Spike", "Gap Fill", "Retest Entry",
      "Micro Trend Reversal", "Intraday Momentum", "Candle Break", "Low Volatility Break"
    ],
    short: [
      "Resistance Rejection", "Breakdown", "Bear Flag", "VWAP Reject", "Overbought Drop",
      "RSI Breakdown", "Momentum Short", "Failed Retest", "Range Rejection", "BTC Weakness",
      "Fade Rally", "High Funding Short", "Wick Reversal", "Distribution Spike", "Double Top",
      "EMA Failure", "Volume Dump", "Lower High", "Trend Exhaustion"
    ]
  },
  Swing: {
    long: [
      "Trend Reversal", "Accumulation Zone", "Higher Low", "Volume Surge", "BTC Stability",
      "Macro Tailwind", "Breakout Retest", "Channel Break", "Price Compression",
      "Momentum Recovery", "Structural Shift", "Swing Continuation", "Base Formation",
      "ATR Expansion", "Support Hold", "Golden Cross", "Bullish Divergence"
    ],
    short: [
      "Distribution Phase", "Breakdown Retest", "Trend Failure", "Bear Channel",
      "BTC Dominance Rise", "Macro Risk Off", "Volume Dump", "Continuation Short",
      "Overextension", "EMA Breakdown", "Top Formation", "Deleveraging", "Supply Zone Test",
      "Bearish Divergence", "Failed Recovery", "Funding Squeeze", "Reversal Confirmation"
    ]
  },
  Quarterly: {
    long: [
      "Macro Bullish", "Cycle Accumulation", "Institutional Flow", "Halving Play",
      "Long Term Trend", "High Conviction", "BTC Accumulation", "HODL Setup",
      "Rotation to Quality", "Market Leadership", "ATF Flow Support", "Technical Base",
      "Uptrend Start", "Reversal Confirmation", "Multiweek Breakout"
    ],
    short: [
      "Macro Bearish", "Overextended Trend", "Cycle Top", "Liquidity Drain",
      "Funding Excess", "Speculation Exhaustion", "Dominance Rise", "Hedge Play",
      "Mean Reversion", "Deleveraging Phase", "Distribution Zone", "Risk Off",
      "Volatility Spike", "Rate Hike Impact", "Macro Headwind", "Structural Breakdown",
      "Long Liquidation", "Top Distribution", "Bear Cycle Entry"
    ]
  }
};

const getCategory = (validity) => {
  if (!validity) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(validity);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (diffDays <= 1) return "Intraday";
  if (diffDays <= 7) return "Swing";
  if (diffDays <= 90) return "Quarterly";
  return null;
};

export default function LogicModal({
  open, onClose,
  opinion, validity,
  scripId, entry, target1, target2, stoploss, leverage, entryRange1, entryRange2, markPrice,
  onPublishSuccess
}) {
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState("");

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setDescription("");
      setSelectedTags([]);
      setAttachments([]);
      setPublishError("");
    }
  }, [open]);

  const category = useMemo(() => getCategory(validity), [validity]);
  const availableTags = useMemo(() => {
    if (!category || !opinion) return [];
    return TAGS[category]?.[opinion] || [];
  }, [category, opinion]);

  const handleTagChange = (event) => {
    const { value } = event.target;
    setSelectedTags(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((t) => t !== tagToRemove));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const fileObjects = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      id: Math.random().toString(36).substring(7),
    }));
    setAttachments((prev) => [...prev, ...fileObjects]);
  };

  const handleRemoveFile = (id) => {
    setAttachments((prev) => {
      const item = prev.find((a) => a.id === id);
      if (item) URL.revokeObjectURL(item.preview);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handlePublish = async () => {
    setPublishing(true);
    setPublishError("");
    try {
      // Step 1: Publish recommendation
      const recoResult = await serverAddr.post("/publisher/recommendations/", {
        scrip_id: scripId,
        direction: opinion.toUpperCase(),
        leverage: Number(leverage),
        entry_price: Number(entry),
        entry_range: [Number(entryRange1), Number(entryRange2)],
        // mark_price: Number(markPrice),
        target_1: Number(target1),
        target_2: Number(target2),
        stop_loss: Number(stoploss),
        target_period: validity
      });

      const recoId = recoResult?.id ?? recoResult?.data?.id;

      // Step 2: Post logic (tags, description, attachments)
      const formData = new FormData();
      formData.append("id", recoId);
      formData.append("logic", description);
      selectedTags.forEach((tag) => formData.append("tags", tag));
      attachments.forEach((item) => formData.append("chart", item.file));

      await uploadServer.post("/publisher/logic/", formData);

      onPublishSuccess?.();
      onClose();
    } catch (err) {
      console.error("Publish failed:", err);
      setPublishError(err?.message || err?.detail || "Failed to publish. Please try again.");
    } finally {
      setPublishing(false);
    }
  };

  const categoryLabel = category
    ? `${category} · ${opinion === "long" ? "Long" : "Short"}`
    : "";

  const charCount = description.length;
  const canPublish = selectedTags.length >= 3 && attachments.length >= 1 && charCount <= 200 && charCount > 0 && !publishing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        Logic <span style={{ color: "red" }}>*</span>
        {categoryLabel && (
          <Typography component="span" sx={{ fontSize: "13px", ml: 1, color: "#aaa", fontWeight: 400 }}>
            ({categoryLabel})
          </Typography>
        )}
      </DialogTitle>

      <DialogContent className="logic-modal">

        {/* Description */}
        <TextField
          multiline
          rows={5}
          fullWidth
          placeholder="Write a brief description..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ "& .MuiInputBase-input": { fontSize: "14px" }, mb: 0.5 }}
        />
        <Typography sx={{ fontSize: "12px", color: charCount > 200 ? "#f44336" : "#aaa", mb: 2 }}>
          {charCount}/200 characters{charCount > 200 ? ` (${charCount - 200} over limit)` : ""}
        </Typography>

        {/* Tags */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography className="field-label">
              Tags <span>*</span> <small>(Min 3)</small>
            </Typography>

            <FormControl fullWidth size="small">
              <Select
                multiple
                displayEmpty
                value={selectedTags}
                onChange={handleTagChange}
                input={<OutlinedInput />}
                className="custom-dropdown"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#fff",
                      color: "#222",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
                      "& .MuiMenuItem-root": {
                        fontSize: "13px",
                        "&:hover": { bgcolor: "#f0f4ff" },
                        "&.Mui-selected": { bgcolor: "#dce9ff", color: "#1a5fd4" },
                        "&.Mui-selected:hover": { bgcolor: "#c9dcff" }
                      }
                    }
                  }
                }}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span style={{ color: "#999" }}>Logic Tags</span>;
                  }
                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip
                          key={val}
                          label={val}
                          size="small"
                          onDelete={() => handleRemoveTag(val)}
                          onMouseDown={(e) => e.stopPropagation()}
                          sx={{
                            fontSize: "11px",
                            background: "#fff",
                            color: "#222",
                            "& .MuiChip-deleteIcon": { color: "#555", "&:hover": { color: "#222" } }
                          }}
                        />
                      ))}
                    </Box>
                  );
                }}
              >
                {availableTags.length === 0 ? (
                  <MenuItem disabled>
                    <em style={{ fontSize: "13px" }}>
                      {validity ? "No tags available for this range" : "Set a validity date first"}
                    </em>
                  </MenuItem>
                ) : (
                  availableTags.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {selectedTags.length > 0 && selectedTags.length < 3 && (
              <Typography sx={{ fontSize: "12px", color: "#f44336", mt: 0.5 }}>
                Select at least 3 tags ({3 - selectedTags.length} more needed)
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Upload */}
        <Box className="upload-row">
          <label className="upload-btn">
            Upload + <span style={{ color: "red" }}>*</span>
            <input type="file" hidden multiple onChange={handleFileChange} />
          </label>
          <Typography className="attachment-count">
            Attachments - {attachments.length}
          </Typography>
        </Box>
        {attachments.length === 0 && (
          <Typography sx={{ fontSize: "12px", color: "#f44336", mt: 0.5 }}>
            At least 1 attachment is required
          </Typography>
        )}

        {/* Preview */}
        <Box className="preview-grid">
          {attachments.map((item) => (
            <Box key={item.id} className="preview-card">
              <img src={item.preview} alt="preview" />
              <IconButton
                size="small"
                className="remove-btn"
                onClick={() => handleRemoveFile(item.id)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        {/* Error */}
        {publishError && (
          <Typography sx={{ color: "#f44336", fontSize: "13px", mt: 2 }}>
            {publishError}
          </Typography>
        )}

        {/* Footer */}
        <Box className="logic-footer">
          <Button variant="outlined" onClick={onClose} disabled={publishing}>
            Cancel
          </Button>
          <Button
            className="publish-btn"
            variant="contained"
            disabled={!canPublish}
            onClick={handlePublish}
            startIcon={publishing ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {publishing ? "Publishing..." : "Publish"}
          </Button>
        </Box>

      </DialogContent>
    </Dialog>
  );
}
